import { api } from '../../../../lib/api';
import type { ClubProfileField, ClubProfileValues } from '../../../../lib/clubProfile';
import type { ClubPostBody } from '../../../../lib/socialComposerAdapter';
import type { ClientExternalCalendarPost } from '../../../../lib/socialCalendar';
import { buildUploadedMedia, validateMediaFile } from '../../../../lib/publishing/mediaUpload';
import type { PostMetrics } from '../../../../lib/publishing/postStats';
import type {
  CaptionDraftsResult,
  CaptionRefineResult,
  GbpConfig,
  PostMedia,
  PostPlatform,
} from '../../../../types/publishing';

// KyckSocial — the one file that names /api/portal/social. Every call is behind
// requirePortalAuth + the `social` permission; the scope is resolved server-side,
// and an `accountId` sent here can only SELECT within it (outside → 404).

export const SOCIAL_BASE = '/api/portal/social';

export const socialJson = (method: string, body: unknown) => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export const socialQuery = (params: Record<string, string | undefined>): string => {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') sp.set(k, v);
  const s = sp.toString();
  return s ? `?${s}` : '';
};

export type ClubProfileDto = {
  accountId: string;
  values: ClubProfileValues;
  updatedAt: string | null;
  updatedBy: string | null;
};

export type SocialAccount = {
  id: string;
  name: string;
  connected: Record<PostPlatform, boolean>;
  /** Server-derived. Booleans only: the service list never reaches the browser. */
  canPost: boolean;
  canInbox: boolean;
};

export type ClientPost = {
  id: string;
  accountId: string;
  status: string;
  content: string;
  media: Array<{ type: string; url: string; thumbnailUrl: string | null; altText: string | null }>;
  targets: Array<{
    platform: PostPlatform;
    fbContentType: string | null;
    igContentType: string | null;
    gbp: GbpConfig | null;
    // The club's own per-placement caption / media for this target. Returned so a
    // reload shows what they chose (the server narrows the media like the base).
    customContent: string | null;
    customMedia: Array<{ type: string; url: string; thumbnailUrl: string | null; altText: string | null }>;
    publishState: string | null;
    publishedUrl: string | null;
    error: string | null;
  }>;
  scheduledFor: string | null;
  publishedAt: string | null;
  publishMode: string;
  publishError: string | null;
  createdByName: string;
  madeByClub: boolean;
  createdAt: string | null;
  updatedAt: string | null;
};

export type ClientExternalResult = {
  posts: ClientExternalCalendarPost[];
  capped: boolean;
  /** Platforms that did not answer; their posts are missing from `posts`. */
  failures: Array<{ platform: string }>;
};

export type ClientPostStats = {
  stats: {
    metrics: PostMetrics | null;
    platforms: Array<{ platform: PostPlatform; measurable: boolean; metrics: PostMetrics | null }>;
    permalink: string | null;
    lastUpdated: string | null;
    lastSync: string | null;
  } | null;
  unavailable: 'gbp-unsupported' | 'not-found' | 'error' | null;
};

export type ClientPostInput =ClubPostBody;

export type CaptionAsk = {
  kyckAccountId: string;
  destinations: Array<'facebook' | 'instagram' | 'googlebusiness'>;
  brief: string;
  tone: string;
  emoji: 'none' | 'light' | 'generous';
  media: PostMedia[];
};

// ⚠️ A PROJECTION. It must satisfy the shared inbox VIEW (src/types/inboxView.ts)
// without a cast, and it must never grow to match the stored thread: staff
// workflow (assignee, claim, priority, tags) is not in it and does not belong.
export type ClientThread = {
  id: string;
  accountId: string;
  channel: 'facebook' | 'instagram' | 'googlebusiness' | 'metaads';
  kind: 'comment' | 'dm' | 'review';
  status: 'new' | 'pending' | 'resolved';
  author: { name: string; avatarUrl: string | null };
  preview: string;
  permalink: string | null;
  postContent: string | null;
  postImageUrl: string | null;
  postPermalink: string | null;
  postCommentCount: number | null;
  postLikeCount: number | null;
  platformPostId: string | null;
  platformCommentId: string | null;
  parentCommentId: string | null;
  ownerAuthored: boolean;
  rating: number | null;
  hasReply: boolean | null;
  messageCount: number;
  lastActivityAt: string;
  lastInboundAt: string | null;
  createdAt: string;
};

export type ClientMessage = {
  id: string;
  threadId: string;
  direction: 'incoming' | 'outgoing';
  authorName: string;
  text: string;
  externalId: string | null;
  hidden: boolean;
  liked: boolean;
  reactionCount: number | null;
  canLike: boolean | null;
  isPrivateReply: boolean;
  attachments: Array<{ type: string; url: string | null; sourceUrl?: string | null }>;
  createdAt: string;
};

export type ClientPostGroup = {
  unresolvedCount: number;
  entries: Array<{ thread: ClientThread; messages: ClientMessage[] }>;
};

export type ThreadFilter = { kind?: string; status?: string; channel?: string; pageSize?: number };

function commentAction(threadId: string, action: string, messageId: string): Promise<unknown> {
  return api(`${SOCIAL_BASE}/threads/${encodeURIComponent(threadId)}/${action}`, socialJson('POST', { messageId }));
}

export const socialApi = {
  getProfile: (accountId: string): Promise<ClubProfileDto> =>
    api(`${SOCIAL_BASE}/profile${socialQuery({ accountId })}`),
  putProfile: (accountId: string, values: Partial<Record<ClubProfileField, string | null>>): Promise<ClubProfileDto> =>
    api(`${SOCIAL_BASE}/profile`, socialJson('PUT', { accountId, ...values })),

  accounts: (): Promise<{ accounts: SocialAccount[] }> => api(`${SOCIAL_BASE}/accounts`),
  posts: (accountId: string): Promise<{ posts: ClientPost[] }> =>
    api(`${SOCIAL_BASE}/posts${socialQuery({ accountId })}`),
  /** Posts made directly on the platforms: a live vendor call, so callers cache it (see ClubCalendar). */
  externalPosts: (accountId: string): Promise<ClientExternalResult> =>
    api(`${SOCIAL_BASE}/posts/external${socialQuery({ accountId })}`),
  post: (id: string): Promise<{ post: ClientPost }> => api(`${SOCIAL_BASE}/posts/${encodeURIComponent(id)}`),
  /** Performance of one of the club's own posts. Scoped server-side by the post's account. */
  postStats: (id: string): Promise<ClientPostStats> => api(`${SOCIAL_BASE}/posts/${encodeURIComponent(id)}/stats`),
  createPost: (input: ClientPostInput): Promise<{ post: ClientPost; dispatchError: string | null }> =>
    api(`${SOCIAL_BASE}/posts`, socialJson('POST', input)),
  updatePost: (id: string, input: ClientPostInput): Promise<{ post: ClientPost; dispatchError: string | null }> =>
    api(`${SOCIAL_BASE}/posts/${encodeURIComponent(id)}`, socialJson('PATCH', input)),
  deletePost: (id: string): Promise<{ ok: true }> =>
    api(`${SOCIAL_BASE}/posts/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  threads: (accountId: string, f: ThreadFilter = {}): Promise<{ threads: ClientThread[]; total: number }> =>
    api(`${SOCIAL_BASE}/threads${socialQuery({ accountId, kind: f.kind, status: f.status, channel: f.channel, pageSize: f.pageSize === undefined ? undefined : String(f.pageSize) })}`),
  thread: (id: string): Promise<{ thread: ClientThread; messages: ClientMessage[] }> =>
    api(`${SOCIAL_BASE}/threads/${encodeURIComponent(id)}`),
  /** Refresh-on-open: pull this conversation / post live (native replies). */
  refreshThread: (id: string): Promise<{ refreshed: boolean; throttled: boolean; messagesUpserted: number }> =>
    api(`${SOCIAL_BASE}/threads/${encodeURIComponent(id)}/refresh`, socialJson('POST', {})),
  /** Every comment on this thread's post, in the club's projection. */
  postGroup: (id: string): Promise<ClientPostGroup> =>
    api(`${SOCIAL_BASE}/threads/${encodeURIComponent(id)}/post-group`),
  // Comment actions. Each names the ONE comment it applies to; hide and delete are
  // public acts on the club's page, and delete is permanent.
  likeComment: (id: string, messageId: string): Promise<unknown> => commentAction(id, 'like', messageId),
  unlikeComment: (id: string, messageId: string): Promise<unknown> => commentAction(id, 'unlike', messageId),
  hideComment: (id: string, messageId: string): Promise<unknown> => commentAction(id, 'hide', messageId),
  unhideComment: (id: string, messageId: string): Promise<unknown> => commentAction(id, 'unhide', messageId),
  deleteComment: (id: string, messageId: string): Promise<unknown> => commentAction(id, 'delete-comment', messageId),
  resolvePost: (id: string): Promise<{ resolved: number }> =>
    api(`${SOCIAL_BASE}/threads/${encodeURIComponent(id)}/resolve-post`, socialJson('POST', {})),
  replyComment: (id: string, message: string, replyToMessageId?: string): Promise<unknown> =>
    api(`${SOCIAL_BASE}/threads/${encodeURIComponent(id)}/reply`, socialJson('POST', { message, ...(replyToMessageId ? { replyToMessageId } : {}) })),
  replyReview: (id: string, message: string): Promise<unknown> =>
    api(`${SOCIAL_BASE}/threads/${encodeURIComponent(id)}/review-reply`, socialJson('POST', { message })),
  sendDm: (id: string, message: string): Promise<unknown> =>
    api(`${SOCIAL_BASE}/threads/${encodeURIComponent(id)}/dm`, socialJson('POST', { message })),
  setStatus: (id: string, status: 'new' | 'pending' | 'resolved'): Promise<unknown> =>
    api(`${SOCIAL_BASE}/threads/${encodeURIComponent(id)}/status`, socialJson('POST', { status })),
  // AI reply drafting: ONLY from a button click.
  suggestReply: (id: string, messageId?: string): Promise<{ drafts: string[]; grounding: string }> =>
    api(`${SOCIAL_BASE}/threads/${encodeURIComponent(id)}/suggest-reply`, socialJson('POST', messageId ? { messageId } : {})),

  // AI: each of these is called ONLY from a button click.
  draftCaption: (ask: CaptionAsk): Promise<CaptionDraftsResult> =>
    api(`${SOCIAL_BASE}/draft-caption`, socialJson('POST', ask)),
  refineCaption: (ask: CaptionAsk & { currentDraft: string; instruction: string }): Promise<CaptionRefineResult> =>
    api(`${SOCIAL_BASE}/refine-caption`, socialJson('POST', ask)),
};

/** Native upload: presign, then the BROWSER PUTs straight to storage (the 32 MiB rule). */
export async function uploadSocialMedia(file: File): Promise<PostMedia> {
  const check = validateMediaFile({ mimeType: file.type, sizeBytes: file.size });
  if (!check.ok) throw new Error(check.message);
  const presign = await api<{ uploadUrl: string; publicUrl: string }>(
    `${SOCIAL_BASE}/media/presign`,
    socialJson('POST', { filename: file.name || 'upload', contentType: check.contentType, size: file.size }),
  );
  const put = await fetch(presign.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': check.contentType },
    body: file,
  });
  if (!put.ok) throw new Error(`Storage rejected the upload (${put.status}). Try again.`);
  return buildUploadedMedia({
    publicUrl: presign.publicUrl,
    filename: file.name || 'upload',
    contentType: check.contentType,
    sizeBytes: file.size,
    source: 'upload',
  });
}
